import {
  Box,
  type ColumnDef,
  type DateOnly,
  exceedsMaxPrecision,
  type FixingIndexMarkMessageData,
  HStack,
  Icon,
  IconName,
  InlineFormattedNumber,
  NumberInput,
  type PriceParams,
  Text,
  Tooltip,
  useCallbackRef,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks';
import type { Fixing } from './fixingSlice';
import type { FixingBlotterRow } from './useFixingBlotterDataObs';
import { getIndexMarkDateOnly } from './utils';

interface UseFixingBlotterColumnsProps {
  onFixingChange: (fixingIndex: string, fixingPrice: string) => void;
}

interface FixingInputCellRendererParams {
  onFixingChange: UseFixingBlotterColumnsProps['onFixingChange'];
}

interface IndexMarkCellValue {
  fixing: Fixing;
  fixingDate: DateOnly | undefined;
  indexMark: FixingIndexMarkMessageData | undefined;
}

export function fixingInputID(rowIndex: number | null) {
  return `fixing-blotter-input-${rowIndex}`;
}

export const useFixingBlotterColumns = ({ onFixingChange }: UseFixingBlotterColumnsProps) => {
  const { showMakerApplyFixingDialogReferencePrice, showMakerApplyFixingDialogIndexMarks } = useFeatureFlag();

  const columns = useMemo(
    () =>
      compact([
        {
          type: 'security',
          field: 'fixing.symbol',
          title: 'Symbol',
          suppressKeyboardEvent: () => true,
          width: 130,
        },
        {
          type: 'fixingIndex',
          field: 'fixing.fixingIndex.Name',
          title: 'Fixing Index',
          width: 105,
          suppressKeyboardEvent: () => true,
        },
        {
          type: 'fixingIndexTimeAtDate',
          field: 'fixing.fixingIndex.Name',
          title: 'Fixing Time',
          width: 220,
          params: {
            dateOnlyField: 'fixingDate',
          },
          suppressKeyboardEvent: () => true,
        },
        {
          type: 'custom',
          id: 'legFixing',
          width: 200,
          title: 'Fixing',
          headerClass: 'ag-right-aligned-cell',
          params: {
            suppressKeyboardEvent: () => true,
            valueGetter: (params: ValueGetterParams<FixingBlotterRow>): Fixing | undefined => {
              // We roll with the entire row's data object as the value of the column to always get change detection properly
              return params.data?.fixing;
            },
            cellRendererParams: { onFixingChange },
            cellRenderer: FixingInputCellRenderer,
          } satisfies ColDef<FixingBlotterRow>,
        },
        showMakerApplyFixingDialogReferencePrice && {
          type: 'price',
          field: 'midPrice',
          title: 'Live Mid Price',
          description: 'A live mid price sampled from select markets provided for your reference.',
          params: {
            assetField: 'fixing.symbol',
          } satisfies PriceParams<FixingBlotterRow>,
          suppressKeyboardEvent: () => true,
        },
        showMakerApplyFixingDialogIndexMarks && {
          type: 'custom',
          id: 'indexMark',
          title: 'Index Mark',
          width: 180,
          headerClass: 'ag-right-aligned-cell',
          params: {
            suppressKeyboardEvent: () => true,
            valueGetter: (params: ValueGetterParams<FixingBlotterRow>): IndexMarkCellValue | undefined => {
              if (params.data == null) {
                return undefined;
              }
              return {
                fixing: params.data.fixing,
                fixingDate: params.data.fixingDate,
                indexMark: params.data.indexMark,
              };
            },
            cellRenderer: IndexMarkCellRenderer,
            valueFormatter: (params: ValueFormatterParams<FixingBlotterRow, IndexMarkCellValue>): string => {
              return params.value?.indexMark?.Mark ?? '';
            },
          } satisfies ColDef<FixingBlotterRow>,
        },
      ] satisfies (ColumnDef<FixingBlotterRow> | false)[]),
    [onFixingChange, showMakerApplyFixingDialogReferencePrice, showMakerApplyFixingDialogIndexMarks]
  );

  return columns;
};

function FixingInputCellRenderer({
  value,
  node,
  context,
  onFixingChange,
}: ICellRendererParams<FixingBlotterRow, Fixing> & FixingInputCellRendererParams) {
  const handleChange = useCallbackRef((fixingPrice: string) => {
    if (!value?.fixingIndex.Name) {
      return;
    }

    onFixingChange(value.fixingIndex.Name, fixingPrice);
  });

  if (!value) {
    return <></>;
  }

  const { field, symbol } = value;
  const legSecurity = symbol ? context.current.securitiesBySymbol?.get(symbol) : undefined;
  const exceedsMinFixingIncrementDps = exceedsMaxPrecision(field.displayValue, value.fixingIndex.MinFixingIncrement);

  return (
    <NumberInput
      value={field.displayValue}
      disabled={field.isDisabled}
      invalid={!!field.errorString}
      onChange={handleChange}
      id={fixingInputID(node.rowIndex)}
      data-testid={`apply-fixing-price-input-${value.fixingIndex.Name}`}
      suffix={legSecurity?.QuoteCurrency}
      prefix={
        exceedsMinFixingIncrementDps ? (
          <Tooltip
            tooltip={`This value exceeds the decimal precision implied by MinFixingIncrement (${value.fixingIndex.MinFixingIncrement}). Submission is still allowed.`}
          >
            <Icon
              icon={IconName.ExclamationSolid}
              color="colorTextWarning"
              data-testid={`apply-fixing-price-precision-warning-${value.fixingIndex.Name}`}
              tabIndex={-1}
            />
          </Tooltip>
        ) : undefined
      }
    />
  );
}

function IndexMarkCellRenderer({ value, context }: ICellRendererParams<FixingBlotterRow, IndexMarkCellValue>) {
  if (!value) {
    return <></>;
  }

  const symbol = value.fixing.symbol;
  const legSecurity = symbol ? context.current.securitiesBySymbol?.get(symbol) : undefined;
  const markDate = getIndexMarkDateOnly(value.indexMark?.NominalTime, value.fixing.fixingIndex.NominalIndexTimeZone);

  if (!value.indexMark) {
    return (
      <Box w="100%">
        <Tooltip tooltip={`No mark found for fixing date ${value.fixingDate}`}>
          <HStack w="100%" justifyContent="flex-end" gap="spacing4">
            <Icon
              icon={IconName.ExclamationSolid}
              color="colorTextWarning"
              data-testid={`apply-fixing-index-mark-missing-${value.fixing.fixingIndex.Name}`}
              tabIndex={-1}
            />

            <Text color="colorTextWarning">No mark found</Text>
          </HStack>
        </Tooltip>
      </Box>
    );
  }

  return (
    <HStack w="100%" justifyContent="flex-end" gap="spacing4">
      <InlineFormattedNumber number={value.indexMark.Mark} currency={legSecurity?.QuoteCurrency} />
      {markDate && (
        <Text color="colorTextSubtle" fontStyle="italic">
          {markDate}
        </Text>
      )}
    </HStack>
  );
}
